import { faHandFist, faKitMedical, faMicrochip, faRadiation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { useState } from "react";
import { type Combatant, type EncounterState, activeAttack } from "@lancer-vtt/domain";
import { STABILIZE_CLEARABLE_CONDITIONS, checkMineDisarmLegality, legalQuickTechOptions, techMenuPick } from "@lancer-vtt/rules";
import { BrutalistRadioGroup } from "../../../routes/home/BrutalistRadioGroup";
import type { StabilizePrimary, StabilizeSecondary } from "../attackIntent";
import { searchTargetReason, mountTargetReason, jockeyTargetReason, rideTargetReason, type ActionDraft } from "../combatInspector";
import { AccuracyStepper } from "./AccuracyStepper";
import { chooserEconomy } from "./inspectorEconomy";

/**
 * The **inline Ram chooser** — expanded in place when the Ram row is armed (`aim.mode === "ram"`). Ram is a single-target
 * melee action (RAW Core p.70): the **target is picked by clicking an adjacent token on the board** (like
 * Skirmish — the host's `fireAt` dispatches `request_attack` with the synthetic `act_ram` weapon when a
 * valid target is clicked), so this panel carries only the optional **knock back 1 space** toggle
 * (pre-declared on the aim as the `ram_push` choice) and the targeting prompt — no in-panel dropdown.
 *
 * **The engine is the wall, the UI only mirrors it** (CLAUDE.md: "UI mirrors the reducer's rules as
 * affordances; it never re-enforces them"): the board **dims** an illegal target (the host's
 * `outOfRangeTargetIds`, off {@link ramTargetReason} — the threat-1 reach wash + `checkRamSize`) so only
 * a valid adjacent token fires; an illegal click falls through to plain selection. The chooser invents no
 * rule and carries no force flag — a target that slips through is the authority's to reject. The push
 * defaults **OFF** (RAW "may also choose" — knock-back is an opt-in), a brutalist checkbox toggle.
 */
export function RamChooser({
  aim,
  setAim,
}: {
  /** The armed draft — guaranteed ram-mode + `attackerId === combatant.id` by the caller. */
  aim: Extract<ActionDraft, { mode: "ram" }>;
  setAim: (a: ActionDraft | null) => void;
}) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Ram">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faHandFist} className="icon-lead" />RAM</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel ram" {...tipAttrs({ label: "Cancel ram", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <RamControls aim={aim} setAim={setAim} />

      <div className="skirmish-hud-target">› click an adjacent target</div>
    </div>
  );
}

/**
 * {@link RamChooser}'s one declaration without its shell: the optional **knock back 1 space**
 * toggle (default OFF, RAW "may also"). The command menu shows the same part in targeting mode's
 * panel while a Ram it armed picks its target (`TokenCommandMenuHost.tsx`).
 */
export function RamControls({ aim, setAim }: { aim: Extract<ActionDraft, { mode: "ram" }>; setAim: (a: ActionDraft | null) => void }) {
  return (
    <label className="skirmish-hud-check">
      <input type="checkbox" aria-label="knock back 1 space" checked={aim.push} onChange={(e) => setAim({ ...aim, push: e.target.checked })} />
      <span>KNOCK BACK 1 SPACE</span>
    </label>
  );
}

/**
 * A directed multi-pick deploy's progress: how many sections are left to place, the row's blurb,
 * and the **back one pick** control once a pick has landed. The command menu shows it in targeting
 * mode's panel while a deploy it armed picks its cells (`TokenCommandMenuHost.tsx`).
 */
export function SitesControls({ total, remaining, canBack, onBack, blurb }: { total: number; remaining: number; canBack: boolean; onBack: () => void; blurb?: string }) {
  return (
    <>
      <div className="skirmish-hud-target">
        › click a target cell on the board — {remaining} of {total} {total === 1 ? "section" : "sections"} left
      </div>
      {blurb ? <div className="skirmish-hud-note">{blurb}</div> : null}
      {canBack && (
        <div className="skirmish-hud-foot">
          <button type="button" className="skirmish-hud-go" onClick={onBack}>
            ‹ back one pick
          </button>
        </div>
      )}
    </>
  );
}

/**
 * The **inline Grapple chooser** — expanded in place when the Grapple row is armed (`aim.mode === "grapple"`). Grapple
 * is a single-target melee action (RAW Core p.72) with **no up-front declaration at all** (unlike Ram's
 * optional knock-back) — the **target is picked by clicking an adjacent token on the board** (the
 * host's `fireAt` dispatches `request_attack` with the synthetic `act_grapple` weapon when a valid
 * target is clicked), so this panel carries only the targeting prompt.
 *
 * **The engine is the wall, the UI only mirrors it** (CLAUDE.md: "UI mirrors the reducer's rules as
 * affordances; it never re-enforces them"): the board **dims** an illegal target (the host's
 * `outOfRangeTargetIds`, off {@link grappleTargetReason} — the threat-1 reach wash, no Size rule unlike
 * Ram) so only a valid adjacent token fires; an illegal click falls through to plain selection. The
 * chooser invents no rule and carries no force flag — a target that slips through is the authority's
 * to reject.
 */
export function GrappleChooser({ setAim }: { setAim: (a: ActionDraft | null) => void }) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Grapple">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faHandFist} className="icon-lead" />GRAPPLE</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel grapple" {...tipAttrs({ label: "Cancel grapple", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <div className="skirmish-hud-target">› click an adjacent target</div>
    </div>
  );
}

/**
 * The **inline Improvised Attack chooser** (RAW Core p.71, item 2; targeting
 * brutalized to match Ram/Grapple/Skirmish) — expanded in place when the Improvised Attack row is
 * armed (`aim.mode === "improvised_attack"`). Improvised Attack is a single-target melee FULL
 * action with **no up-front declaration at all** (the exact Grapple pattern) — the **target is
 * picked by clicking an adjacent token on the board** (the host's `fireAt` dispatches
 * `request_attack` with the synthetic `act_improvised_attack` weapon when a valid target is
 * clicked), so this panel carries only the targeting prompt.
 *
 * **The engine is the wall, the UI only mirrors it** (CLAUDE.md: "UI mirrors the reducer's rules as
 * affordances; it never re-enforces them"): the board **dims** an illegal target (the host's
 * `outOfRangeTargetIds`, off {@link improvisedAttackTargetReason} — the threat-1 reach wash, no Size
 * rule) so only a valid adjacent token fires; an illegal click falls through to plain selection. The
 * chooser invents no rule and carries no force flag — a target that slips through is the authority's
 * to reject. Unlike Ram/Grapple, a hit here deals REAL 1d6 kinetic damage — resolved server-side,
 * nothing this chooser needs to know.
 */
export function ImprovisedAttackChooser({ setAim }: { setAim: (a: ActionDraft | null) => void }) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Improvised Attack">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faHandFist} className="icon-lead" />IMPROVISED ATTACK</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel improvised attack" {...tipAttrs({ label: "Cancel improvised attack", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <div className="skirmish-hud-target">› click an adjacent target</div>
    </div>
  );
}

/**
 * Search's pick: the Hidden + in-Sensors-range target select ({@link searchTargetReason}) and
 * SEARCH, or the empty state that says why there is nothing to search. The command menu's Search
 * card renders it (`token-command-menu` 3.2); a click on the Hidden token also fires it
 * (`aimResolvesToFire`, `decisions/0188`).
 */
export function SearchControls({ combatant: c, state, onFire }: { combatant: Combatant; state: EncounterState; onFire: (targetId: string) => void }) {
  const candidates = state.order
    .map((id) => state.combatants[id])
    .filter((t): t is Combatant => !!t && t.id !== c.id && searchTargetReason(state, c, t) === null);
  const [target, setTarget] = useState("");

  return candidates.length === 0 ? (
    <div className="skirmish-hud-empty">{SEARCH_EMPTY}</div>
  ) : (
    <>
      <select
        aria-label="search target"
        className="skirmish-hud-select"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      >
        <option value="">target…</option>
        {candidates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <div className="skirmish-hud-foot">
        <span className="skirmish-hud-note">{SEARCH_NOTE}</span>
        <button
          type="button"
          className="skirmish-hud-apply"
          aria-label="search"
          disabled={target === ""}
          onClick={() => onFire(target)}
        >
          SEARCH
        </button>
      </div>
    </>
  );
}

/** Search's rule-stating copy (Hidden targets in Sensors range, Core p.70), registered in `rulesCopyRegistry.ts`. */
export const SEARCH_EMPTY = "NO HIDDEN TARGETS IN SENSORS RANGE";
export const SEARCH_NOTE = "HIDDEN + IN SENSORS RANGE ONLY";

/** The mines `actorId` may disarm in `state` (the viewer's projection): each live mine the server's `checkMineDisarmLegality` passes. */
export function disarmableMines(state: EncounterState, actorId: string): Combatant[] {
  return state.order
    .map((id) => state.combatants[id])
    .filter((m): m is Combatant => !!m && m.mineProfile !== undefined && checkMineDisarmLegality(state, actorId, m.id).ok);
}

/**
 * The command menu's Disarm Mine card (Core p.106): Search's pick shape over the mines this unit may
 * disarm, filtered through the server's own `checkMineDisarmLegality` (an adjacent, live mine), and
 * DISARM. The list reads only the viewer's projected state, which holds no mine hidden from it, so
 * it offers exactly the detected mines plus the viewer's own. The check itself is drawn server-side.
 */
export function DisarmMineControls({ combatant: c, state, onFire }: { combatant: Combatant; state: EncounterState; onFire: (mineId: string) => void }) {
  const candidates = disarmableMines(state, c.id);
  const [target, setTarget] = useState("");

  return candidates.length === 0 ? (
    <div className="skirmish-hud-empty">{DISARM_EMPTY}</div>
  ) : (
    <>
      <select aria-label="mine to disarm" className="skirmish-hud-select" value={target} onChange={(e) => setTarget(e.target.value)}>
        <option value="">mine…</option>
        {candidates.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
      <div className="skirmish-hud-foot">
        <span className="skirmish-hud-note">{DISARM_NOTE}</span>
        <button type="button" className="skirmish-hud-apply" aria-label="disarm" disabled={target === ""} onClick={() => onFire(target)}>
          DISARM
        </button>
      </div>
    </>
  );
}

/** Disarm Mine's rule-stating copy (adjacent mines only; a failed check detonates it, Core p.106), registered in `rulesCopyRegistry.ts`. */
export const DISARM_EMPTY = "NO DETECTED MINE ADJACENT";
export const DISARM_NOTE = "ADJACENT ONLY · A FAILED CHECK DETONATES IT";

/**
 * A pilot verb's unit pick without its shell (Mount, Jockey, Ride): the legal targets' select and the
 * commit button, or the empty state. The command menu's chooser cards render it
 * (`pilot-command-menu` 1.4).
 */
function UnitPickControls({ candidates, verb, empty, note, onFire }: { candidates: Combatant[]; verb: string; empty: string; note: string; onFire: (targetId: string) => void }) {
  const [target, setTarget] = useState("");
  const lower = verb.toLowerCase();
  return candidates.length === 0 ? (
    <div className="skirmish-hud-empty">{empty}</div>
  ) : (
    <>
      <select aria-label={`${lower} target`} className="skirmish-hud-select" value={target} onChange={(e) => setTarget(e.target.value)}>
        <option value="">mech…</option>
        {candidates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <div className="skirmish-hud-foot">
        <span className="skirmish-hud-note">{note}</span>
        <button type="button" className="skirmish-hud-apply" aria-label={lower} disabled={target === ""} onClick={() => onFire(target)}>
          {verb}
        </button>
      </div>
    </>
  );
}

type PilotPickProps = {
  combatant: Combatant;
  state: EncounterState;
  /** Dispatch the verb's intent for the picked mech (the handler clears the aim after sending). */
  onFire: (mechId: string) => void;
};

/**
 * Mount's pick (RAW Core p.70). **Mount is a UNIT pick, not a cell pick** — the on-foot pilot
 * climbs into a specific adjacent Unpiloted mech by id, so this mirrors {@link SearchControls}'
 * shape (a `<select>` + a fire button), never `legalPilotBridgeCells` (that is
 * for Dismount/Eject, the MECH's own board-cell-targeted actions). The candidate filter calls the
 * real exported `checkMountLegality` (`@lancer-vtt/rules`, via {@link mountTargetReason}) per
 * candidate — mech kind, Unpiloted with `partner` pointing at THIS pilot, and adjacency are all the
 * wall's own conjuncts, never re-derived here.
 */
export function MountControls({ combatant: c, state, onFire }: PilotPickProps) {
  const candidates = state.order.map((id) => state.combatants[id]).filter((t): t is Combatant => !!t && t.kind === "mech" && mountTargetReason(state, c, t) === null);
  return <UnitPickControls candidates={candidates} verb="MOUNT" empty="NO ADJACENT UNPILOTED MECH OF YOURS" note="YOUR OWN ADJACENT UNPILOTED MECH ONLY" onFire={onFire} />;
}

/**
 * Jockey's pick (RAW Core p.75): Mount's shape, targeting an adjacent HOSTILE mech-scale combatant
 * by id. The candidate filter calls the real exported `checkJockeyLegality` (via
 * {@link jockeyTargetReason}) per candidate — hostility, adjacency, not-already-jockeying/jockeyed,
 * not-Destroyed. The GRIT-vs-HULL contest is drawn entirely server-side; this previews no roll.
 */
export function JockeyControls({ combatant: c, state, onFire }: PilotPickProps) {
  const candidates = state.order.map((id) => state.combatants[id]).filter((t): t is Combatant => !!t && (t.kind === "mech" || t.kind === "npc") && jockeyTargetReason(state, c, t) === null);
  return <UnitPickControls candidates={candidates} verb="JOCKEY" empty="NO ADJACENT HOSTILE MECH" note="ADJACENT HOSTILE MECH ONLY — CONTESTED GRIT vs. HULL" onFire={onFire} />;
}

/**
 * Ride's pick (RAW Core p.70): {@link JockeyControls}' sibling with the side test inverted and no
 * contest (a willing ally always attaches). The candidate filter calls the real exported
 * `checkRideLegality` (via {@link rideTargetReason}) per candidate.
 */
export function RideControls({ combatant: c, state, onFire }: PilotPickProps) {
  const candidates = state.order.map((id) => state.combatants[id]).filter((t): t is Combatant => !!t && t.kind === "mech" && rideTargetReason(state, c, t) === null);
  return <UnitPickControls candidates={candidates} verb="RIDE" empty="NO WILLING ADJACENT ALLIED MECH" note="WILLING ADJACENT ALLIED MECH ONLY — NO CONTEST" onFire={onFire} />;
}

/**
 * The **inline Stabilize chooser** — the mech FULL SUPPORT-action
 * twin of {@link BarrageChooser}, expanded in place when the Stabilize row is armed (`aim.mode ===
 * "stabilize"`). Stabilize is the deterministic (no-dice) self-maintenance Full action (RAW Core p.70):
 * choose **one** primary — **Cool** (clear all heat + remove Exposed) or **Patch** (spend one repair →
 * restore all HP) — **then** one **secondary** — reload Loading weapons, clear Burn, clear one condition,
 * or end one save-ending effect — both collected UP FRONT (the BarrageChooser pattern), then **STABILIZE**
 * dispatches ONE `request_stabilize` intent carrying both.
 *
 * **The engine is the wall, the UI only mirrors it** (CLAUDE.md: "UI mirrors the reducer's rules as
 * affordances; it never re-enforces them"): the **Patch** option is **disabled + annotated** when the mech
 * has 0 repairs remaining — the SAME hard-reject the server applies (`checkStabilizeLegality` in
 * `economy.ts`) — but the chooser invents no rule and carries no force flag. A Patch that slips through is
 * the authority's to reject; the categorized rejection toast (browser-rejection-feedback) surfaces it. The
 * condition / save-effect secondaries are populated from the mech's **carried** statuses (the only ones a
 * clear can actually affect — the `clear_condition` menu intersects the RAW
 * {@link STABILIZE_CLEARABLE_CONDITIONS} list; `end_save_effect` lists carried `save_ends` statuses).
 */
export function StabilizeChooser({
  combatant: c,
  setAim,
  repairsLeft,
  onFire,
}: {
  combatant: Combatant;
  setAim: (a: ActionDraft | null) => void;
  /** Repairs left in the mech's Repair Cap (`current.resources[REPAIR_CAP_RESOURCE]`) — gates the Patch affordance. */
  repairsLeft: number;
  /** Dispatch the assembled Stabilize (the host packs `request_stabilize` + clears the aim on send). */
  onFire: (primary: StabilizePrimary, secondary?: StabilizeSecondary, secondaryStatus?: string) => void;
}) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Stabilize">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faKitMedical} className="icon-lead" />STABILIZE</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel stabilize" {...tipAttrs({ label: "Cancel stabilize", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <StabilizeControls combatant={c} repairsLeft={repairsLeft} onFire={onFire} />
    </div>
  );
}

/**
 * Why STABILIZE is greyed (the dev-feedback rule: a greyed reason stays inline): a Patch with no
 * repairs left (Core p.70, the server's own wall), or a condition or save-effect secondary that
 * names nothing yet. Exported as copy data for `rulesCopyRegistry.ts`.
 */
export function stabilizeBlockReason(args: { primary: StabilizePrimary; canPatch: boolean; secondary: StabilizeSecondary | "" }): string {
  if (args.primary === "patch" && !args.canPatch) return "No repairs remaining";
  return args.secondary === "end_save_effect" ? "Pick the effect to end" : "Pick the condition to clear";
}

/**
 * {@link StabilizeChooser}'s form without its shell: the primary and secondary switchers, the
 * status pick and STABILIZE. The command menu's Stabilize card renders this same part
 * (`token-command-menu` 3.2).
 */
export function StabilizeControls({
  combatant: c,
  repairsLeft,
  onFire,
}: {
  combatant: Combatant;
  repairsLeft: number;
  onFire: (primary: StabilizePrimary, secondary?: StabilizeSecondary, secondaryStatus?: string) => void;
}) {
  const canPatch = repairsLeft > 0;
  // Default the primary to Cool (always legal — no resource cost); fall back from Patch if repairs run dry.
  const [primary, setPrimary] = useState<StabilizePrimary>("cool");
  const [secondary, setSecondary] = useState<StabilizeSecondary | "">("");
  const [secondaryStatus, setSecondaryStatus] = useState("");

  // The status options for the condition / save-effect secondaries — drawn from the mech's CARRIED
  // statuses (a clear only ever affects a status the actor actually has). clear_condition intersects the
  // RAW listed conditions; end_save_effect lists carried `save_ends` effects. Each is the menu, not a
  // wall — the engine clears whatever name it is handed (a non-carried pick is a no-op + a warning).
  const conditionOpts = c.statuses.filter((s) => STABILIZE_CLEARABLE_CONDITIONS.includes(s.name)).map((s) => s.name);
  const saveEffectOpts = c.statuses.filter((s) => s.expiry.kind === "save_ends").map((s) => s.name);
  const needsStatus = secondary === "clear_condition" || secondary === "end_save_effect";
  const statusOpts = secondary === "clear_condition" ? conditionOpts : secondary === "end_save_effect" ? saveEffectOpts : [];

  // FIRE gates: a primary is always set (Cool by default); a condition/save-effect secondary needs a
  // status named (else the clear is a no-op). Patch is gated by the affordance (the radio disables at 0),
  // so a 0-repair Patch can't be the chosen primary here — but the server stays the wall regardless.
  const ready = (primary !== "patch" || canPatch) && (!needsStatus || secondaryStatus !== "");

  const onSecondary = (next: StabilizeSecondary | "") => {
    setSecondary(next);
    setSecondaryStatus(""); // reset the status pick when the secondary changes
  };

  return (
    <div className="skirmish-hud-body">
      {/* PRIMARY — a segmented switcher; Cool always legal, Patch disabled at 0 repairs (the server wall mirrored). */}
      <BrutalistRadioGroup<StabilizePrimary>
        name="stabilize-primary"
        label="Primary"
        value={primary}
        onChange={setPrimary}
        options={[
          { value: "cool", label: "Cool" },
          { value: "patch", label: "Patch", disabled: !canPatch, title: canPatch ? undefined : "No repairs remaining" },
        ]}
        caption={
          <>
            {primary === "cool"
              ? "Clear all heat + remove Exposed"
              : `Spend 1 repair → restore all HP (${repairsLeft} left)`}
            {!canPatch && <span className="skirmish-hud-caption-warn"> · Patch: no repairs left</span>}
          </>
        }
      />

      {/* SECONDARY — a segmented switcher (or None). A condition / save-effect choice reveals a status picker. */}
      <BrutalistRadioGroup<StabilizeSecondary | "">
        name="stabilize-secondary"
        label="Secondary"
        value={secondary}
        onChange={onSecondary}
        options={[
          { value: "", label: "None", ariaLabel: "no secondary" },
          { value: "reload", label: "Reload" },
          { value: "clear_burn", label: "Clear Burn" },
          { value: "clear_condition", label: "Clear Condition" },
          { value: "end_save_effect", label: "End Save-Effect" },
        ]}
      />

      {needsStatus && (
        <label className="skirmish-hud-field">
          <span className="skirmish-hud-field-label">{secondary === "clear_condition" ? "Condition" : "Effect"}</span>
          <select
            aria-label="stabilize secondary status"
            value={secondaryStatus}
            onChange={(e) => setSecondaryStatus(e.target.value)}
            className="skirmish-hud-select"
          >
            <option value="">{statusOpts.length ? "choose…" : "(none carried)"}</option>
            {statusOpts.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="skirmish-hud-foot">
        {!ready && <span className="skirmish-hud-note">{stabilizeBlockReason({ primary, canPatch, secondary })}</span>}
        <button
          type="button"
          aria-label="fire stabilize"
          disabled={!ready}
          onClick={() => onFire(primary, secondary === "" ? undefined : secondary, needsStatus ? secondaryStatus : undefined)}
          className="skirmish-hud-go"
        >
          STABILIZE
        </button>
      </div>
    </div>
  );
}

/**
 * The **inline Self-Destruct chooser** — the mech QUICK
 * SUPPORT-action twin of {@link StabilizeChooser}'s no-board-target shape, expanded in place when the
 * Self-Destruct row is armed (`aim.mode === "self_destruct"`). Self-Destruct is a self-only **Quick**
 * action (RAW Core p.72) that arms (or re-arms) the reactor-meltdown countdown at a chosen detonation
 * turn — RAW's "your choice" of "the end of your next turn, or one of your turns within the following
 * two rounds." The three radios are labelled by **effect**, not the raw `at` integer — a bare 1/2/3 is
 * unreadable at a table and invites the wrong pick on an irreversible action.
 *
 * **The arm→FIRE two-step IS the confirmation this irreversible self-kill deserves** — no extra
 * confirm modal (no precedent anywhere in this codebase; out of scope). The FIRE control is styled
 * DESTRUCTIVE (`.skirmish-hud-go.is-danger`, an additive modifier on the shared `.skirmish-hud*`
 * family — no new stylesheet) and the caption spells out the consequence up front: annihilates this
 * mech, kills its pilot, BURST 2 / 4d6 explosive to everything nearby. **The engine is the wall, this
 * chooser invents no rule**: `checkSelfDestructLegality` (`economy.ts`) is the only gate (`at`'s 1..3
 * range, nothing else), and RAW attaches no cancel clause — a GM `resource set` on the countdown is the
 * fiat backstop, not a client affordance.
 */
export function SelfDestructChooser({
  combatant: c,
  setAim,
  onFire,
}: {
  combatant: Combatant;
  setAim: (a: ActionDraft | null) => void;
  /** Dispatch the assembled Self-Destruct (the host packs `request_self_destruct` + clears the aim on send). */
  onFire: (at: 1 | 2 | 3) => void;
}) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Self-Destruct">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faRadiation} className="icon-lead" />SELF-DESTRUCT</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel self-destruct" {...tipAttrs({ label: "Cancel self-destruct", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <SelfDestructControls combatant={c} onFire={onFire} />
    </div>
  );
}

/**
 * {@link SelfDestructChooser}'s form without its shell: the detonation-turn pick, the irreversible
 * warning and DETONATE. The command menu's Self-Destruct card renders this same part
 * (`token-command-menu` 3.2).
 */
export function SelfDestructControls({ combatant: c, onFire }: { combatant: Combatant; onFire: (at: 1 | 2 | 3) => void }) {
  // Default to `1` ("end of my next turn") — the earliest legal detonation, never pre-selecting a
  // later turn a player didn't ask for.
  const [at, setAt] = useState<"1" | "2" | "3">("1");

  return (
    <div className="skirmish-hud-body">
      <BrutalistRadioGroup<"1" | "2" | "3">
        name="self-destruct-at"
        label="Detonate at"
        value={at}
        onChange={setAt}
        options={[
          { value: "1", label: "End of my next turn" },
          { value: "2", label: "…the turn after" },
          { value: "3", label: "…the turn after that" },
        ]}
        caption={
          <span className="skirmish-hud-caption-warn">
            IRREVERSIBLE — annihilates {c.name}, kills its pilot, and causes a BURST 2 explosion (4d6
            explosive; AGILITY save halves) to everything nearby.
          </span>
        }
      />

      <div className="skirmish-hud-foot">
        <button
          type="button"
          aria-label="fire self-destruct"
          onClick={() => onFire(Number(at) as 1 | 2 | 3)}
          className="skirmish-hud-go is-danger"
        >
          DETONATE
        </button>
      </div>
    </div>
  );
}

/** Whether the armed tech option is a tech attack (Invade), the one option that rolls, so the one with an ACC / DIFF stepper. */
export function techOptionIsAttack(combatant: Combatant, optionId: string): boolean {
  const pick = techMenuPick(optionId).option;
  return legalQuickTechOptions({ npcActor: combatant.kind === "npc" }).some((o) => o.id === pick && o.route.kind === "tech_attack");
}

/**
 * **The armed tech option's controls** — Invade's ACC / DIFF stepper ({@link AccuracyStepper}), read
 * into `aim.accuracy`; nothing for an option that does not roll (Scan, Lock On, Bolster). Lifted out
 * of {@link QuickTechChooser} and {@link FullTechChooser} (`token-command-menu` 3.2) so the command
 * menu's Quick Tech and Full Tech cards render the SAME control rather than a fork.
 */
export function TechAimControls({
  combatant: c,
  aim,
  setAim,
}: {
  combatant: Combatant;
  aim: Extract<ActionDraft, { mode: "quick_tech" | "full_tech" }>;
  setAim: (a: ActionDraft | null) => void;
}) {
  if (!techOptionIsAttack(c, aim.optionId)) return null;
  return <AccuracyStepper value={aim.accuracy} onChange={(accuracy) => setAim({ ...aim, accuracy })} />;
}

/**
 * The **inline Quick Tech chooser** — the mech/NPC TECH Quick-action surface, expanded in place when the Quick
 * Tech row is armed (`aim.mode === "quick_tech"`). Quick Tech (RAW Core p.67) is a "choose one" tech
 * action.
 *
 * Mirrors {@link SkirmishChooser}'s weapon-card pick: this panel lists the four legal options
 * ({@link legalQuickTechOptions}) as brutalist rows, and clicking one arms it on the SAME aim
 * (`setAim({...aim, optionId: sel ? "": o.id, accuracy: 0 })` — re-clicking the armed row disarms
 * it, toggle-style) — there is **no per-row target select anymore**. **The target is picked by
 * clicking a token on the board**: the host's `fireAt` (`CombatApp.tsx`) dispatches through the
 * EXISTING {@link quickTechIntent} router once an option is armed (Invade → a tech `request_attack`;
 * Lock On / Bolster / Scan → a `request_activate`). Invade's accuracy/difficulty stepper stays here,
 * on the armed option's own card, read into `aim.accuracy` — the click-to-fire twin of Skirmish's
 * accuracy stepper.
 *
 * **The engine is the wall, the UI only mirrors it**: the board **dims** an ineligible
 * target (the host's `outOfRangeTargetIds`, off {@link techTargetReason} — Sensors range,
 * tech-immunity, self, the willing-ally-Invade deferral) so only a legal token fires; an illegal click
 * falls through to plain selection. **"Used this turn" grays the WHOLE row**, not a per-option state —
 * all four options share the RAW once-per-turn `quick_tech` counter, read via `usedUp` on the QUICK
 * TECH row itself, so this chooser carries no separate per-option/per-session gray-out.
 */
export function QuickTechChooser({
  combatant: c,
  aim,
  setAim,
}: {
  combatant: Combatant;
  /** The armed draft — guaranteed quick_tech-mode + `attackerId === combatant.id` by the caller. */
  aim: Extract<ActionDraft, { mode: "quick_tech" }>;
  setAim: (a: ActionDraft | null) => void;
}) {
  // The legal options: a PC actor sees all four; an NPC actor is
  // narrowed to Invade/Lock On (RAW's NPC Quick Tech text) — the target-tech-immune seam prunes
  // further once a target token is clicked (`techTargetReason`, board-side).
  const options = legalQuickTechOptions({ npcActor: c.kind === "npc" });
  const armed = options.find((o) => o.id === aim.optionId);

  return (
    <div className="skirmish-hud" role="group" aria-label="Quick Tech">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faMicrochip} className="icon-lead" />QUICK TECH</span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel quick tech" {...tipAttrs({ label: "Cancel quick tech", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <div className="skirmish-hud-weapons">
        {options.map((o) => {
          const sel = o.id === aim.optionId;
          return (
            <button
              key={o.id}
              type="button"
              className={`skirmish-hud-wpn${sel ? " is-sel" : ""}`}
              aria-pressed={sel}
              onClick={() => setAim({ ...aim, optionId: sel ? "" : o.id, accuracy: 0 })}
            >
              <span className="skirmish-hud-wpn-top">
                <span className="skirmish-hud-wpn-name">{o.name}</span>
              </span>
            </button>
          );
        })}
      </div>

      <TechAimControls combatant={c} aim={aim} setAim={setAim} />

      <div className="skirmish-hud-target">{armed ? "› click a target" : "› pick an option, then click a target"}</div>
    </div>
  );
}

/**
 * The **inline Full Tech chooser** — the mech/NPC TECH Full-action surface, expanded in place when
 * the Full Tech row is armed
 * (`aim.mode === "full_tech"`). Full Tech (RAW Core p.71: "two quick tech options, may repeat the
 * same one") takes TWO Quick Tech picks under one Full charge.
 *
 * **Stateless, mirroring {@link QuickTechChooser}'s row-arm gesture exactly**: clicking an option row
 * arms it on the SAME aim (`setAim({...aim, optionId: sel ? "": o.id, accuracy: 0 })`) — there is
 * **no per-row target select**. **The target is picked by clicking a token on the board**: the host's
 * `fireAt` (`CombatApp.tsx`) dispatches ONE `request_full_tech` pick per click via
 * {@link buildFullTechIntent} (opener charges the Full action + opens the mid-attack lock;
 * continuation is free and auto-detected off the lock, exactly like Barrage), then **re-arms**
 * `aim.optionId` back to `""` (instead of clearing the aim) so this SAME chooser stays live for pick
 * 2 — the repeat-same-option case (Bolster+Bolster, RAW Core p.71) needs no special handling, since
 * every option row stays clickable regardless of what already fired.
 *
 * **Progress + auto-close are read from the live `AttackLock`** (`activeAttack(state.activation)`,
 * mirrors {@link BarrageChooser} exactly — never local state): `lock.weapons.length` is how many
 * picks have fired this Full Tech (0 → 1 → 2), and the server **auto-closes the lock at 2 picks** —
 * once it's gone (and no fresh Full action remains to open a new one) the chooser reads COMPLETE,
 * the same "BARRAGE COMPLETE" shape Barrage's chooser already uses.
 *
 * **The engine is the wall, the UI only mirrors it**: the board dims an ineligible target
 * for the armed option (`CombatApp.tsx`'s `outOfRangeTargetIds`, off `techTargetReason` — identical
 * wiring to Quick Tech's), and the opener/continuation legality (`checkFullTechOpener`/
 * `checkFullTechContinuation`, repeats explicitly legal) + per-pick Sensors/LoS/immunity walls stay
 * the authority's.
 */
export function FullTechChooser({
  combatant: c,
  state,
  aim,
  setAim,
  onEndAttack,
}: {
  combatant: Combatant;
  /**
   * The state every economy read below composes against — the caller hands over
   * {@link InspectorEconomy}`.state` (`combatWindows/inspectorEconomy.ts`), which is the real
   * `EncounterState` in every ordinary case and `previewActivation`'s hypothetical
   * post-activation state when this unit is `"ready"` and the viewer controls it (2.3 / D4). That
   * is deliberate and load-bearing: the room folds a real `activate_unit` in front of such a
   * unit's first action and runs its own gate ladder against exactly this object, so composing
   * the untouched reads against it is what keeps this panel and the server from disagreeing about
   * a pre-activation Full Tech. Read-only — never dispatched, never applied (see
   * `previewActivation`'s never-commit invariant).
   */
  state: EncounterState;
  /** The armed draft — guaranteed full_tech-mode + `attackerId === combatant.id` by the caller. */
  aim: Extract<ActionDraft, { mode: "full_tech" }>;
  setAim: (a: ActionDraft | null) => void;
  /** Dispatch `end_attack` — the abandon-after-one close. */
  onEndAttack: () => void;
}) {
  // Same narrowing as QuickTechChooser: an NPC actor only offers
  // Invade/Lock On as its Full Tech sub-options.
  const options = legalQuickTechOptions({ npcActor: c.kind === "npc" });
  const FULL_TECH_COUNT = 2; // RAW: a Full Tech is exactly two Quick Tech picks (may repeat the same one).
  // `turnOpen` — this unit's turn is the one on the `state` prop: genuinely mid-turn, or the
  // previewed turn its first action would fold open (2.3 / D4 — see the `state` prop's doc).
  const { turnOpen, fullAvailable } = chooserEconomy(state, c.id);
  // The mid-attack lock is the source of truth for progress — only the acting actor mid-Full-Tech has one.
  // A previewed turn carries no `attack` at all, so a pre-activation Full Tech reads as an opener.
  const lock = turnOpen ? activeAttack(state.activation) : null;
  const fired = lock?.weapons.length ?? 0;
  const midAttack = lock !== null;
  // Terminal: the Full Tech has closed (no lock) AND there is no fresh Full action to open a new one —
  // mirrors BarrageChooser's `complete` exactly (the same "spent this turn" shape).
  const complete = turnOpen && !midAttack && !fullAvailable;
  const armed = options.find((o) => o.id === aim.optionId);

  return (
    <div className="skirmish-hud" role="group" aria-label="Full Tech">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title">
          <Icon icon={faMicrochip} />
          <Icon icon={faMicrochip} className="icon-lead" />FULL TECH
        </span>
        <button type="button" className="skirmish-hud-x" onClick={() => setAim(null)} aria-label="cancel full tech" {...tipAttrs({ label: "Cancel full tech", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      {!turnOpen ? (
        <div className="skirmish-hud-empty">ACTIVATE this mech's turn to Full Tech.</div>
      ) : complete ? (
        <div className="skirmish-hud-empty">FULL TECH COMPLETE — Full action spent.</div>
      ) : (
        <>
          {midAttack && (
            <div className="skirmish-hud-target">
              FULL TECH IN PROGRESS — {fired} OF {FULL_TECH_COUNT} FIRED · pick an option, then click a target
            </div>
          )}

          <div className="skirmish-hud-weapons">
            {options.map((o) => {
              const sel = o.id === aim.optionId;
              return (
                <button
                  key={o.id}
                  type="button"
                  className={`skirmish-hud-wpn${sel ? " is-sel" : ""}`}
                  aria-pressed={sel}
                  onClick={() => setAim({ ...aim, optionId: sel ? "" : o.id, accuracy: 0 })}
                >
                  <span className="skirmish-hud-wpn-top">
                    <span className="skirmish-hud-wpn-name">{o.name}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <TechAimControls combatant={c} aim={aim} setAim={setAim} />

          <div className="skirmish-hud-target">{armed ? "› click a target" : "› pick an option, then click a target"}</div>

          {midAttack && (
            <div className="skirmish-hud-foot">
              <button type="button" className="skirmish-hud-go" onClick={onEndAttack}>
                END FULL TECH
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
